import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { PROJECT_CATEGORIES } from '@/constants/project-categories'

export const categories = PROJECT_CATEGORIES

export const getMainCategoryFromSubCategory = (subCategory: string): string | null => {
  for (const category of categories) {
    if (category.subCategories.includes(subCategory as any)) return category.mainCategory
  }
  return null
}

export const convertSubCategoriesToMainCategories = (subCategories: string[]): string[] => {
  const main = new Set<string>()
  for (const sub of subCategories) {
    const m = getMainCategoryFromSubCategory(sub)
    if (m) main.add(m)
  }
  return Array.from(main)
}

interface CategorySelection {
  mainCategories: string[] // max 2
  subCategories: string[] // per main max 3
}

type Props = {
  value?: CategorySelection | string[] // legacy
  onChange: (newValues: CategorySelection) => void
}

const FieldDropBar = ({ value = { mainCategories: [], subCategories: [] }, onChange }: Props) => {
  const normalizedValue: CategorySelection = useMemo(() => {
    if (Array.isArray(value)) {
      const mainCategories = convertSubCategoriesToMainCategories(value).slice(0, 2)
      return { mainCategories, subCategories: value }
    }
    return value
  }, [value])

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<CategorySelection>(normalizedValue)
  const [selectedMain, setSelectedMain] = useState<string | null>(normalizedValue.mainCategories[0] ?? null)

  // Dialog 열 때마다 현재 value를 draft로 동기화 (취소 시 원복 UX)
  useEffect(() => {
    if (!open) return
    setDraft(normalizedValue)
    setSelectedMain(normalizedValue.mainCategories[0] ?? categories[0]?.mainCategory ?? null)
  }, [open, normalizedValue])

  // selectedMain 유효성 보정
  useEffect(() => {
    if (!open) return
    if (selectedMain && draft.mainCategories.includes(selectedMain)) return
    if (draft.mainCategories.length > 0) setSelectedMain(draft.mainCategories[0])
  }, [open, draft.mainCategories, selectedMain])

  const getSelectedSubCount = (mainCategory: string) =>
    draft.subCategories.filter((sub) => getMainCategoryFromSubCategory(sub) === mainCategory).length

  const toggleMainCategory = (mainCategory: string) => {
    const isSelected = draft.mainCategories.includes(mainCategory)

    if (isSelected) {
      const cat = categories.find((c) => c.mainCategory === mainCategory)
      const toRemove = new Set(cat?.subCategories ?? [])
      setDraft((prev) => ({
        mainCategories: prev.mainCategories.filter((m) => m !== mainCategory),
        subCategories: prev.subCategories.filter((s) => !toRemove.has(s)),
      }))
      return
    }

    if (draft.mainCategories.length >= 2) return
    setDraft((prev) => ({ ...prev, mainCategories: [...prev.mainCategories, mainCategory] }))
    setSelectedMain(mainCategory)
  }

  const toggleSubCategory = (subCategory: string) => {
    const main = getMainCategoryFromSubCategory(subCategory)
    if (!main) return

    const isSelected = draft.subCategories.includes(subCategory)
    if (isSelected) {
      setDraft((prev) => ({ ...prev, subCategories: prev.subCategories.filter((s) => s !== subCategory) }))
      return
    }

    const count = draft.subCategories.filter((s) => getMainCategoryFromSubCategory(s) === main).length
    if (count >= 3) return

    setDraft((prev) => ({ ...prev, subCategories: [...prev.subCategories, subCategory] }))
  }

  const hasSelection = normalizedValue.mainCategories.length > 0 || normalizedValue.subCategories.length > 0

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start min-h-[44px] text-left whitespace-normal break-words bg-white border border-gray-200 hover:bg-gray-50 rounded-xl"
        >
          {hasSelection ? (
            <div className="flex flex-col items-start gap-1 w-full">
              {normalizedValue.mainCategories.length > 0 && (
                <span className="text-xs text-gray-500">
                  {normalizedValue.mainCategories.join(', ')}
                </span>
              )}
              <div className="text-sm text-gray-900">
                {normalizedValue.subCategories.slice(0, 2).join(', ')}
                {normalizedValue.subCategories.length > 2 && (
                  <span className="text-gray-500"> · 외 {normalizedValue.subCategories.length - 2}개</span>
                )}
              </div>
            </div>
          ) : (
            <span className="text-sm text-gray-500">서비스 유형을 선택하세요</span>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent
        className={[
          // ✅ 모바일: 화면 상단 기준으로 붙이고(중앙정렬 제거) 잘림 방지
          'w-[calc(100vw-2rem)] max-w-2xl rounded-xl p-0',
          'left-1/2 top-4 translate-x-[-50%] translate-y-0',

          // ✅ sm 이상: 기존처럼 중앙정렬로 복귀(원하면 유지)
          'sm:top-1/2 sm:-translate-y-1/2',

          // ✅ 전체 높이 제한 + 내부 스크롤 구조를 위해 overflow는 컨테이너로
          'max-h-[calc(100dvh-2rem)]',
        ].join(' ')}
      >
        {/* ✅ 헤더/본문/푸터 3단 레이아웃 */}
        <div className="flex max-h-[calc(100dvh-2rem)] flex-col">
          {/* Header (고정) */}
          <div className="px-4 py-3 border-b border-gray-100">
            <DialogHeader>
              <DialogTitle className="text-base font-semibold text-gray-900">분야 선택</DialogTitle>
              <DialogDescription className="sr-only">
                프로젝트 분야를 대분류와 소분류로 선택하세요
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Body (스크롤) */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Main */}
              <div className="sm:border-r sm:pr-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">대분류</h4>
                  <span className="text-xs text-gray-500">{draft.mainCategories.length}/2</span>
                </div>

                {/* ✅ 여기 max-h 제거(바깥이 스크롤 담당) */}
                <div className="space-y-1">
                  {categories.map((c) => {
                    const isSelected = draft.mainCategories.includes(c.mainCategory)
                    const isDisabled = !isSelected && draft.mainCategories.length >= 2

                    return (
                      <div
                        key={c.mainCategory}
                        role="button"
                        tabIndex={isDisabled ? -1 : 0}
                        onClick={() => !isDisabled && toggleMainCategory(c.mainCategory)}
                        onKeyDown={(e) => {
                          if (isDisabled) return
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            toggleMainCategory(c.mainCategory)
                          }
                        }}
                        className={[
                          'w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm border',
                          isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50',
                          isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                        ].join(' ')}
                      >
                        <span className="text-gray-900">{c.mainCategory}</span>
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          onCheckedChange={() => !isDisabled && toggleMainCategory(c.mainCategory)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Sub */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">소분류</h4>
                  {selectedMain && (
                    <span className="text-xs text-gray-500">{getSelectedSubCount(selectedMain)}/3</span>
                  )}
                </div>

                {!selectedMain ? (
                  <div className="text-sm text-gray-500 border border-gray-200 rounded-xl p-4 bg-white">
                    대분류를 먼저 선택해주세요
                  </div>
                ) : (
                  <div className="space-y-1">
                    {categories
                      .find((c) => c.mainCategory === selectedMain)
                      ?.subCategories.map((sub) => {
                        const isSelected = draft.subCategories.includes(sub)
                        const count = getSelectedSubCount(selectedMain)
                        const isDisabled = !isSelected && count >= 3

                        return (
                          <div
                            key={sub}
                            role="button"
                            tabIndex={isDisabled ? -1 : 0}
                            onClick={() => !isDisabled && toggleSubCategory(sub)}
                            onKeyDown={(e) => {
                              if (isDisabled) return
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                toggleSubCategory(sub)
                              }
                            }}
                            className={[
                              'w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm border',
                              isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50',
                              isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                            ].join(' ')}
                          >
                            <span className="text-gray-900">{sub}</span>
                            <Checkbox
                              checked={isSelected}
                              disabled={isDisabled}
                              onCheckedChange={() => !isDisabled && toggleSubCategory(sub)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )
                      })}
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="text-xs text-gray-500">
                대분류 {draft.mainCategories.length}/2 · 소분류 {draft.subCategories.length}개
              </div>

              {draft.subCategories.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {draft.subCategories.map((sub) => (
                    <div
                      key={sub}
                      className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1"
                    >
                      <span className="text-xs text-gray-700">{sub}</span>
                      <button
                        type="button"
                        className="text-xs text-gray-500 hover:text-blue-600"
                        onClick={() => toggleSubCategory(sub)}
                        aria-label={`${sub} 삭제`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer (고정) */}
          <div className="px-4 py-3 border-t border-gray-100">
            <DialogFooter className="m-0 flex gap-2 sm:justify-end">
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => setDraft({ mainCategories: [], subCategories: [] })}
              >
                초기화
              </Button>

              <DialogClose asChild>
                <Button
                  className="rounded-xl"
                  onClick={() => {
                    onChange(draft) // ✅ 여기서만 커밋
                  }}
                >
                  선택
                </Button>
              </DialogClose>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>

    </Dialog>
  )
}

export default FieldDropBar
